import { MathUtils, Vector2, Vector3 } from 'three';
import {
  HighwayArea,
  Segment,
  StationaryPoiSpec,
  LoadHandlingPoiSpec,
  HighwayPoiSpec,
  Areas,
  Group,
  Templates,
  FlowLayer,
  JustInTimeRoutingConfig,
} from '@thive/fddm-types';

import { assertNever } from '@/helpers/utils';
import { getVectorRotatedAroundPoint } from '@/modules/common/helpers/math';
import { tuplify } from '@/modules/common/helpers/array';
import { isHikStorage, isLassie, isLassieMini, isShuttleRack } from '@/modules/templating/guards';

import {
  isHighwayArea,
  isManualArea,
  isRackingArea,
  isSingularStationArea,
  isStationaryArea,
} from './typeguards';
import { Area, SegmentSpatial2D, SpatialArea2D, Template } from '../types';
import {
  getHighwayPoiSpecDefault,
  LOAD_HANDLING_POI_SPEC_DEFAULT,
  POI_SPEC_DEFAULT,
} from '../constants/areas';
import { AtLeast1, minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { SHUTTLE_LOAD_CARRIERS_IDS } from '@/modules/vehicles/constants';
import { RectangleUtils } from './RectangleUtils';

type Groups = Templates & { functionalGroups?: Group[] };

export function hasNoAreas(areas: Areas): boolean {
  return (
    !areas ||
    (areas.highwayAreas.length === 0 &&
      areas.rackingAreas.length === 0 &&
      areas.singularStationAreas.length === 0 &&
      areas.stationaryAreas.length === 0 &&
      areas.manualAreas.length === 0)
  );
}

export function hasNoTemplates(templates: Templates): boolean {
  return (
    !templates ||
    (templates.lassies.length === 0 &&
      templates.lassieMinis.length === 0 &&
      templates.shuttleRacks.length === 0 &&
      (!templates.hikStorages || templates.hikStorages.length === 0))
  );
}

/**
 * @description joins all separate collections under Areas in 1 flat collection
 */
export const areasToFlatCollection = (areas: Areas): Area[] => {
  const allAreas: Area[] = Object.values(areas).flat();

  return allAreas;
};

/**
 * @description searches the collections within Areas, returning the first area to match the provided id, or undefined if no match
 */
export const findAreaById = (areas: Areas, id: string): Area | undefined =>
  areas.highwayAreas.find((item) => item.id === id) ||
  areas.rackingAreas.find((item) => item.id === id) ||
  areas.singularStationAreas.find((item) => item.id === id) ||
  areas.stationaryAreas.find((item) => item.id === id) ||
  areas.manualAreas.find((item) => item.id === id);

/**
 * @description returns an id -> area map with the areas from all collections within the provided Areas
 */
export const areasToFlatMap = (areas: Areas) => {
  const outMap: Map<string, Area> = new Map();

  areas.highwayAreas.forEach((item) => outMap.set(item.id, item));
  areas.rackingAreas.forEach((item) => outMap.set(item.id, item));
  areas.singularStationAreas.forEach((item) => outMap.set(item.id, item));
  areas.stationaryAreas.forEach((item) => outMap.set(item.id, item));
  areas.manualAreas.forEach((item) => outMap.set(item.id, item));

  return outMap;
};

/**
 * @description while keeping the Areas structure, only returns the areas of which the id is provided in the provided ids.
 */
export const filterAreasByIds = (areas: Areas, ids: string[] | Set<string>): Areas => {
  const idsSet = Array.isArray(ids) ? new Set(ids) : ids;
  return {
    highwayAreas: areas.highwayAreas.filter((item) => idsSet.has(item.id)),
    rackingAreas: areas.rackingAreas.filter((item) => idsSet.has(item.id)),
    singularStationAreas: areas.singularStationAreas.filter((item) => idsSet.has(item.id)),
    stationaryAreas: areas.stationaryAreas.filter((item) => idsSet.has(item.id)),
    manualAreas: areas.manualAreas.filter((item) => idsSet.has(item.id)),
  };
};

/**
 * @description while keeping the Areas structure, filters out any areas which id are in the provided ids.
 */
export const excludeAreasByIds = (areas: Areas, ids: string[] | Set<string>): Areas => {
  const idsSet = Array.isArray(ids) ? new Set(ids) : ids;
  return {
    highwayAreas: areas.highwayAreas.filter((item) => !idsSet.has(item.id)),
    rackingAreas: areas.rackingAreas.filter((item) => !idsSet.has(item.id)),
    singularStationAreas: areas.singularStationAreas.filter((item) => !idsSet.has(item.id)),
    stationaryAreas: areas.stationaryAreas.filter((item) => !idsSet.has(item.id)),
    manualAreas: areas.manualAreas.filter((item) => !idsSet.has(item.id)),
  };
};

export function createEmptyAreas(): Areas {
  return {
    highwayAreas: [],
    rackingAreas: [],
    singularStationAreas: [],
    stationaryAreas: [],
    manualAreas: [],
  };
}

export function createEmptyTemplates(): Templates {
  return {
    lassies: [],
    lassieMinis: [],
    shuttleRacks: [],
    hikStorages: [],
  };
}

export function createEmptyFlowLayer(): FlowLayer {
  return {
    dependentFlows: [],
    flows: [],
    flowStops: [],
  };
}

/**
 * @description allocates an area in the designated collection under Areas
 */
export const pushAreaToItsCollection = (areas: Areas, area: Area) => {
  if (isHighwayArea(area)) {
    areas.highwayAreas.push(area);
    return;
  }
  if (isRackingArea(area)) {
    areas.rackingAreas.push(area);
    return;
  }
  if (isSingularStationArea(area)) {
    areas.singularStationAreas.push(area);
    return;
  }
  if (isStationaryArea(area)) {
    areas.stationaryAreas.push(area);
    return;
  }
  if (isManualArea(area)) {
    areas.manualAreas.push(area);
    return;
  }

  assertNever(area);
};

/**
 * @description allocates all areas from source to the designated target collection under Areas
 */
export const pushAreasToItsCollection = (areasTarget: Areas, areasSource: Areas) => {
  areasToFlatCollection(areasSource).forEach((area) => {
    pushAreaToItsCollection(areasTarget, area);
  });
};

/**
 * @description returns an id -> group map with the groups from all collections within the provided Groups
 */
export const groupsToFlatMap = (groups: Templates) => {
  const outMap: Map<string, Template> = new Map();

  groups.lassies.forEach((item) => outMap.set(item.group.id, item));
  groups.lassieMinis.forEach((item) => outMap.set(item.group.id, item));
  groups.shuttleRacks.forEach((item) => outMap.set(item.group.id, item));
  if (groups.hikStorages) {
    groups.hikStorages.forEach((item) => outMap.set(item.group.id, item));
  }

  return outMap;
};

/**
 * @description while keeping the Templates structure, only returns the templates of which the id is provided in the provided ids.
 */
export const filterTemplatesByIds = (
  templates: Templates,
  ids: string[] | Set<string>,
): Templates => {
  const idsSet = Array.isArray(ids) ? new Set(ids) : ids;
  return {
    lassies: templates.lassies.filter((item) => idsSet.has(item.group.id)),
    lassieMinis: templates.lassieMinis.filter((item) => idsSet.has(item.group.id)),
    shuttleRacks: templates.shuttleRacks.filter((item) => idsSet.has(item.group.id)),
    hikStorages: templates.hikStorages
      ? templates.hikStorages.filter((item) => idsSet.has(item.group.id))
      : [],
  };
};

/**
 * @description while keeping the templates structure, filters out any template which id are in the provided ids.
 */
export const excludeTemplatesByIds = (
  templates: Templates,
  ids: string[] | Set<string>,
): Groups => {
  const idsSet = Array.isArray(ids) ? new Set(ids) : ids;
  return {
    lassies: templates.lassies.filter((item) => !idsSet.has(item.group.id)),
    lassieMinis: templates.lassieMinis.filter((item) => !idsSet.has(item.group.id)),
    shuttleRacks: templates.shuttleRacks.filter((item) => !idsSet.has(item.group.id)),
    hikStorages: templates?.hikStorages?.filter((item) => !idsSet.has(item.group.id)),
  };
};

/**
 * @description joins all separate collections under Templates in 1 flat collection
 */
export const templatesToFlatCollection = (groups: Templates): Template[] => {
  const allGroups: Template[] = Object.values(groups).flat();

  return allGroups;
};

/**
 * @description allocates an group in the designated collection under Groups
 */
export const pushTemplateToItsCollection = (templates: Templates, template: Template) => {
  if (isLassie(template)) {
    templates.lassies.push(template);
    return;
  }

  if (isHikStorage(template)) {
    templates?.hikStorages?.push(template);
    return;
  }

  if (isLassieMini(template)) {
    templates.lassieMinis.push(template);
    return;
  }

  if (isShuttleRack(template)) {
    templates.shuttleRacks.push(template);
    return;
  }

  assertNever(template);
};

export const buildTagMatchRegExp = (tags: string[]): RegExp => {
  if (tags.length === 0) return /(?:)/;
  return new RegExp(`\\b(?:${tags.join('|')})\\b`);
};

export const getSupportedVehicleIds = (area: Area): string[] => {
  if (isStationaryArea(area)) return area.stationaryPoiSpecs.map((item) => item.vehicleTypeId);
  if (isSingularStationArea(area))
    return area.loadHandlingPoiSpecs.map((item) => item.vehicleTypeId);
  if (isHighwayArea(area)) return area.highwayPoiSpecs.map((item) => item.vehicleTypeId);
  if (isRackingArea(area)) return [area.vehicleTypeId];
  if (isManualArea(area)) return []; // do nothing, handled in insertion/extraction

  assertNever(area);
};

export const setSupportedVehicles = <T extends Area>(area: T, vehicleIds: string[]): T => {
  if (isStationaryArea(area)) {
    const existingPoiSpec = area.stationaryPoiSpecs.at(0);
    const propertyValuesToInheritFromExistingPoiSpec: Partial<StationaryPoiSpec> = existingPoiSpec
      ? {
          crabSetting: existingPoiSpec.crabSetting,
          curvature: existingPoiSpec.curvature,
        }
      : {};

    const existingPoiSpecsToRetain = area.stationaryPoiSpecs.filter((item) =>
      vehicleIds.includes(item.vehicleTypeId),
    );
    const newPoiSpecs = vehicleIds
      .filter((id) => !area.stationaryPoiSpecs.some((item) => item.vehicleTypeId === id))
      .map((id) => {
        const defaultSpec = POI_SPEC_DEFAULT();

        return {
          ...defaultSpec,
          ...propertyValuesToInheritFromExistingPoiSpec,
          vehicleTypeId: id,
          idGenerator: {
            ...defaultSpec.idGenerator,
            format: `${area.name}%02d`,
          },
        };
      });

    const updatedPoiSpecs: StationaryPoiSpec[] = [...existingPoiSpecsToRetain, ...newPoiSpecs];

    return {
      ...area,
      stationaryPoiSpecs: tuplify(updatedPoiSpecs),
    };
  }
  if (isSingularStationArea(area)) {
    const existingPoiSpec = area.loadHandlingPoiSpecs.at(0);
    const propertyValuesToInheritFromExistingPoiSpec: Partial<LoadHandlingPoiSpec> = existingPoiSpec
      ? {
          alwaysFitStation: existingPoiSpec.alwaysFitStation,
          angle: existingPoiSpec.angle,
          crabSetting: existingPoiSpec.crabSetting,
          curvature: existingPoiSpec.curvature,
          deltaX: existingPoiSpec.deltaX,
          deltaY: existingPoiSpec.deltaY,
          canBeUsedForParking: existingPoiSpec.canBeUsedForParking,
          parkingExtension: existingPoiSpec.parkingExtension,
        }
      : {};

    const existingPoiSpecsToRetain = area.loadHandlingPoiSpecs.filter((item) =>
      vehicleIds.includes(item.vehicleTypeId),
    );

    const newPoiSpecs = vehicleIds
      .filter((id) => !area.loadHandlingPoiSpecs.some((item) => item.vehicleTypeId === id))
      .map((id) => {
        const defaultSpec = LOAD_HANDLING_POI_SPEC_DEFAULT();

        return {
          ...defaultSpec,
          ...propertyValuesToInheritFromExistingPoiSpec,
          vehicleTypeId: id,
          idGenerator: {
            ...defaultSpec.idGenerator,
            format: `${area.name}%02d`,
          },
        };
      });

    const updatedPoiSpecs: LoadHandlingPoiSpec[] = [...existingPoiSpecsToRetain, ...newPoiSpecs];

    return {
      ...area,
      loadHandlingPoiSpecs: tuplify(updatedPoiSpecs),
    };
  }
  if (isHighwayArea(area)) {
    const supportedHighwayVehicleIds = vehicleIds.filter(
      (vehicleId) => !SHUTTLE_LOAD_CARRIERS_IDS.includes(vehicleId),
    );

    // Prep key values from existing poi so the poispecs won't conflict each other (road type etc)
    const existingPoiSpec = area.highwayPoiSpecs.at(0);
    const propertyValuesToInheritFromExistingPoiSpec: Partial<HighwayPoiSpec> = existingPoiSpec
      ? {
          roadType: existingPoiSpec.roadType,
          verticalSpacing: existingPoiSpec.verticalSpacing,
          verticalDistribution: existingPoiSpec.verticalDistribution,
          horizontalSpacing: existingPoiSpec.horizontalSpacing,
          horizontalDistribution: existingPoiSpec.horizontalDistribution,
          curvature: existingPoiSpec.curvature,
          crabSetting: existingPoiSpec.crabSetting,
        }
      : {};

    const existingPoiSpecsToRetain = area.highwayPoiSpecs.filter((item) =>
      supportedHighwayVehicleIds.includes(item.vehicleTypeId),
    );
    const newPoiSpecs = supportedHighwayVehicleIds
      .filter((id) => !area.highwayPoiSpecs.some((item) => item.vehicleTypeId === id))
      .map((id) => {
        const highwayPoiSpecDefault = getHighwayPoiSpecDefault();

        return {
          ...highwayPoiSpecDefault,
          ...propertyValuesToInheritFromExistingPoiSpec,
          vehicleTypeId: id,
          idGenerator: {
            ...highwayPoiSpecDefault.idGenerator,
            format: `${area.name}%04d`,
          },
        };
      });

    const updatedPoiSpecs: HighwayPoiSpec[] = [...existingPoiSpecsToRetain, ...newPoiSpecs];

    return {
      ...area,
      highwayPoiSpecs: tuplify(updatedPoiSpecs),
    };
  }
  if (isRackingArea(area)) {
    return {
      ...area,
      vehicleTypeId: vehicleIds[0],
    };
  }

  if (isManualArea(area)) {
    // do nothing, handled in insertion/extraction
    return area;
  }

  assertNever(area);
};

export const angleBetweenVectors = (fromVector: Vector2, toVector: Vector2): number => {
  const vectorA = new Vector3(fromVector.x, fromVector.y, 0);
  const vectorB = new Vector3(toVector.x, toVector.y, 0);
  let angle = vectorA.angleTo(vectorB);
  const orientation = vectorA.x * vectorB.y - vectorA.y * vectorB.x;
  if (orientation > 0) angle = -angle;

  return angle;
};

export const getAngleBetweenSegments = (fromSegment: Segment, toSegment: Segment): number => {
  const vectorA = new Vector2(
    fromSegment.end.x - fromSegment.start.x,
    fromSegment.end.y - fromSegment.start.y,
  );
  const vectorB = new Vector2(
    toSegment.end.x - toSegment.start.x,
    toSegment.end.y - toSegment.start.y,
  );

  const angle = vectorA.angleTo(vectorB);
  return Math.min(angle, Math.PI - angle);
};

export function segmentToPolygon(seg: SegmentSpatial2D): Vector2[] {
  const dx = seg.end.x - seg.start.x;
  const dy = seg.end.y - seg.start.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const nx = -dy / len;
  const ny = dx / len;

  const lw = seg.leftSideWidth;
  const rw = seg.rightSideWidth;

  const p1 = new Vector2(seg.start.x + nx * lw, seg.start.y + ny * lw);
  return [
    p1,
    new Vector2(seg.start.x - nx * lw, seg.start.y - ny * lw),
    new Vector2(seg.end.x - nx * rw, seg.end.y - ny * rw),
    new Vector2(seg.end.x + nx * rw, seg.end.y + ny * rw),
    p1.clone(),
  ];
}

export function segmentsToPolygon(segments: AtLeast1<Segment>): Vector2[] {
  const points: Vector2[] = [];

  const polygons = segments.map(segmentToPolygon);
  for (let i = 0; i < polygons.length; i++) {
    const currentPolygon = polygons[i];
    points.push(currentPolygon[0], currentPolygon[1]);
  }
  for (let i = polygons.length - 1; i >= 0; i--) {
    const currentPolygon = polygons[i];
    points.push(currentPolygon[2], currentPolygon[3]);
  }
  points.push(polygons[0][0]);

  return points;
}

export function SpatialArea2DToPolygon(shape: SpatialArea2D): Vector2[] {
  const spatialType = shape.type;
  switch (spatialType) {
    case 'segment': {
      return segmentToPolygon(shape.data);
    }
    case 'rectangle': {
      return RectangleUtils.rectangleToPolygon(shape.data);
    }
    default:
      assertNever(spatialType);
  }
}

export const polygonFromAreaOrSegment = (
  areaOrSegment:
    | { type: 'area'; entity: Exclude<Area, HighwayArea> }
    | { type: 'segment'; entity: Segment },
): Vector2[] | null => {
  const { type, entity } = areaOrSegment;

  switch (type) {
    case 'area':
      return RectangleUtils.rectangleToPolygon(entity.rectangle);
    case 'segment':
      return segmentToPolygon(entity);
    default:
      assertNever(type);
  }
};

export const polygonFromArea = (area: Area): Vector2[] | null => {
  const { areaType } = area;

  switch (areaType) {
    case 'HIGHWAY':
      return segmentsToPolygon(area.segments);
    case 'MANUAL':
    case 'RACKING':
    case 'SINGULAR':
    case 'STATIONARY':
      return RectangleUtils.rectangleToPolygon(area.rectangle);
    default:
      assertNever(areaType);
  }
};

export const findSegmentContainingPoint = (
  segments: Segment[],
  point: { x: number; y: number },
): Segment | undefined => {
  for (let i = 0; i < segments.length; ++i) {
    const { start, end, leftSideWidth, rightSideWidth } = segments[i];

    const startV = new Vector2(start.x, start.y);
    const endV = new Vector2(end.x, end.y);
    const pointV = new Vector2(point.x, point.y);

    const segmentEndFromStart = endV.clone().sub(startV);
    const angleInDegrees = MathUtils.radToDeg(
      Math.atan2(segmentEndFromStart.y, segmentEndFromStart.x),
    );

    // rotate segment and point so the segment-middle-line falls on the x-axis
    const axisAlignedSegmentVector = getVectorRotatedAroundPoint(
      segmentEndFromStart,
      new Vector2(0, 0),
      -angleInDegrees,
    );
    const respectivePoint = getVectorRotatedAroundPoint(
      pointV.clone().sub(startV),
      new Vector2(0, 0),
      -angleInDegrees,
    );

    if (
      respectivePoint.y <= leftSideWidth &&
      respectivePoint.y >= -rightSideWidth &&
      respectivePoint.x >= 0 &&
      respectivePoint.x <= axisAlignedSegmentVector.x
    ) {
      return segments[i];
    }
  }

  return undefined;
};

export const offsetSegments = (segments: AtLeast1<Segment>, offset: Vector2): AtLeast1<Segment> =>
  minItemsArrayUtils.mapAtLeast1(segments, (segment) => ({
    ...segment,
    start: new Vector2(segment.start.x, segment.start.y).add(offset),
    end: new Vector2(segment.end.x, segment.end.y).add(offset),
  }));

export const getAreasRelatedToJitConfigs = (
  jitRoutingConfigs: JustInTimeRoutingConfig[],
  getHighwayBySegmentId: FloorplanDesignStore['getHighwayBySegmentId'],
): string[] => {
  const segmentIds = jitRoutingConfigs.map((config) => config.highwayAreaId);
  const highways = segmentIds.map((id) => getHighwayBySegmentId(id));
  const highwayIds = highways.map((area) => area?.id).filter((id) => id !== undefined) as string[];
  const targetAreaIds = jitRoutingConfigs.flatMap((config) => config.targetAreaIds);
  const waitAreaIds = jitRoutingConfigs.flatMap((config) => config.waitAreaIds);
  return [...highwayIds, ...targetAreaIds, ...waitAreaIds];
};
